import { string } from 'yup';
import { StorageKeys } from '../enum/storage-keys';
import { MyListing } from '../services/listing.service';
import { GeoCodeResult } from '../types/geo-code-result.type';
import { storeSet } from './utils';
import { debounce } from 'lodash';
import { Booking } from 'hotdesk_typescript';
import { BookingStatusArray, BookingStatusTabs } from '@/hotdesk-ui/enum/booking-status';
import { geoDebug } from './debug-geo';
import { loadGoogleMaps } from '@/hotdesk-ui/helpers/google-maps-loader';


declare global {
    interface Window {
        google: any;
    }
}

export const updateRecentSearches = (
    placeInfo: GeoCodeResult,
    recentSearches: GeoCodeResult[]
) => {
    const lastSearch = placeInfo;
    sessionStorage.setItem(StorageKeys.LAST_SEARCH, JSON.stringify(lastSearch));
    if (
        !recentSearches.some(
            (search) => search.formatted_address === placeInfo.formatted_address
        )
    ) {
        recentSearches.unshift(placeInfo);
        storeSet(StorageKeys.RECENT_SEARCH, recentSearches);
    }
};

export const serializeGooglePlaceData = (place: any): GeoCodeResult => {
    const {
        address_components,
        formatted_address,
        geometry,
        place_id,
        plus_code,
        types
    } = place;

    // Helper to extract specific component by type
    const getComponent = (type: string) =>
        address_components?.find((comp: any) => comp.types.includes(type));

    // Extract specific components
    const locationZip = getComponent('postal_code')?.long_name;
    const country = getComponent('country')?.long_name;
    const countrySort = getComponent('country')?.short_name;
    const city =
        getComponent('locality')?.long_name ||
        getComponent('administrative_area_level_1')?.long_name;
    const state = getComponent('administrative_area_level_1')?.long_name;

    return {
        address_components: address_components?.map((comp: any) => ({
            long_name: comp.long_name,
            short_name: comp.short_name,
            types: comp.types
        })),
        formatted_address,
        geometry: {
            location: {
                lat: typeof geometry.location.lat === 'number' ? geometry.location.lat : geometry.location.lat(),
                lng: typeof geometry.location.lng === 'number' ? geometry.location.lng : geometry.location.lng()
            },
            location_type: geometry.location_type
        },
        place_id,
        plus_code: plus_code
            ? {
                  compound_code: plus_code.compound_code,
                  global_code: plus_code.global_code
              }
            : { compound_code: '', global_code: '' },
        types,
        locationZip,
        country,
        country_sort: countrySort,
        city,
        state,
        lat: typeof geometry.location.lat === 'number' ? geometry.location.lat : geometry.location.lat(),
        log: typeof geometry.location.lng === 'number' ? geometry.location.lng : geometry.location.lng()
    };
};

/** A picked place that is a whole state, not a city inside one. */
const isStatePlace = (types?: string[]) =>
    !!types?.includes('administrative_area_level_1') &&
    !types.includes('locality');

/**
 * For a picked state, attach its English name as `region_name` so the search
 * can filter by state (see `geoAreaScope`). The API matches states by name,
 * and Places answers in the browser's language — an Arabic browser gets
 * "كاليفورنيا" — so ask for English explicitly. If that lookup fails, keep the
 * localized name only when it has Latin letters the API can match; otherwise
 * leave it unset and the search falls back to a radius around the state.
 */
export const withRegionName = (
    result: GeoCodeResult,
    done: (data: GeoCodeResult) => any
) => {
    if (!isStatePlace(result.types) || !result.place_id) {
        done(result);
        return;
    }
    const fallback = () =>
        done(
            result.state && /[a-z]/i.test(result.state)
                ? { ...result, region_name: result.state }
                : result
        );
    try {
        const service = new window.google.maps.places.PlacesService(
            document.createElement('div')
        );
        service.getDetails(
            {
                placeId: result.place_id,
                fields: ['address_components'],
                language: 'en'
            },
            (place: any, status: string) => {
                const english =
                    status === 'OK'
                        ? place?.address_components?.find((comp: any) =>
                              comp.types.includes('administrative_area_level_1')
                          )?.long_name
                        : undefined;
                if (english) done({ ...result, region_name: english });
                else fallback();
            }
        );
    } catch {
        fallback();
    }
};

export const initializeAutoComplete = (
    inputElement: any,
    onPlaceChange: (data: GeoCodeResult) => any
) => {
    const handlePlaceChanged = debounce((autocompleteInstance) => {
        let place = autocompleteInstance.getPlace();
        if (place && place.formatted_address) {
            if (
                place &&
                place.geometry &&
                place.geometry.location &&
                place.formatted_address
            ) {
                withRegionName(serializeGooglePlaceData(place), onPlaceChange);
            } else {
                console.error('Place not found or missing location.');
            }
        }
    }, 300);

    loadGoogleMaps()
        .then(() => {
            if (!inputElement.current) return;
            let autocomplete = new window.google.maps.places.Autocomplete(
                inputElement.current
            );
            autocomplete.addListener('place_changed', () => {
                handlePlaceChanged(autocomplete);
            });
        })
        .catch((err) => console.error(err));
};

/**
 * Resolve a Google Places prediction (by place_id) to a full GeoCodeResult.
 * Used by the custom suggestions dropdown, which fetches predictions via
 * AutocompleteService and needs geometry/address_components on selection.
 */
export const getPlaceDetailsById = (
    placeId: string,
    callback: (data: GeoCodeResult) => any
) => {
    loadGoogleMaps().then(
        () => {
            const service = new window.google.maps.places.PlacesService(
                document.createElement('div')
            );
            service.getDetails(
                {
                    placeId,
                    fields: [
                        'address_components',
                        'formatted_address',
                        'geometry',
                        'place_id',
                        'plus_code',
                        'types'
                    ]
                },
                (place: any, status: string) => {
                    if (status === 'OK' && place && place.geometry) {
                        withRegionName(serializeGooglePlaceData(place), callback);
                    } else {
                        console.error('Place details not found.', status);
                    }
                }
            );
        },
        () => console.error('Google Maps Places is not available.')
    );
};

export const getDefaultLocation = (
    latitude: number,
    longitude: number,
    callback: (data: GeoCodeResult) => any
) => {
    loadGoogleMaps().then(
        () => {
            geoDebug('getDefaultLocation: geocoding', { latitude, longitude });
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode(
                { location: { lat: latitude, lng: longitude } },
                (results: any, status: string) => {
                    geoDebug('getDefaultLocation: geocode response', {
                        status,
                        hasResult: Boolean(results?.[0])
                    });
                    if (status === 'OK' && results[0]) {
                        // Use Google's top result (often a street address) — same as
                        // QA/prod before we preferred locality ("Cairo").
                        const serialized = serializeGooglePlaceData(results[0]);
                        // Egyptian street addresses carry no `locality`, so the
                        // city fell back to the governorate and the explore
                        // heading read "in Cairo Governorate". Take the city
                        // from the locality-level result Google also returns.
                        const locality = results
                            .find((r: any) => r.types?.includes('locality'))
                            ?.address_components?.find((c: any) =>
                                c.types.includes('locality')
                            )?.long_name;
                        if (locality) serialized.city = locality;
                        geoDebug('getDefaultLocation: serialized', {
                            country_sort: serialized.country_sort,
                            country: serialized.country,
                            city: serialized.city,
                            formatted_address: serialized.formatted_address
                        });
                        callback(serialized);
                    }
                }
            );
        },
        () => console.error('Google Maps is not available.')
    );
};

export const convertToCartesian = (
    listings: MyListing[],
    type:string
) => {
    // Averaging an empty list divides by zero and yields NaN coordinates,
    // which blank out any map they're used to centre. Callers treat 0,0 as
    // "no centre known" and fall back to the searched location.
    if (!listings.length) {
        return { lat: 0, lng: 0, transformArray: [] as object[] };
    }
    let x = 0, y = 0, z = 0;
    const uniqueCoordinates: number[] = [];
    const transformArray: object[] = [];
    let listingCounter = 0;
    let commonWorkspaceArray:MyListing[] = [];
    for (const listing of listings) {
        const workspaceId = listing.workspace?.id as number;
        const listingLocation = typeof listing.location === 'string' ? JSON.parse(listing.location) : listing.location;
        
        const lat = (type === 'workspaces') ? listing.workspace?.location?.coordinates?.[1] as number: listingLocation.coordinates?.[1] as number;
        const lng = (type === 'workspaces') ? listing.workspace?.location?.coordinates?.[0] as number: listingLocation.coordinates?.[0] as number;
        
        if (!uniqueCoordinates.includes(lat)) {
            commonWorkspaceArray = [];
            listingCounter = 1;
            uniqueCoordinates.push(lat);
            commonWorkspaceArray.push(listing);
            const Object = {
                counter: listingCounter,
                listings: commonWorkspaceArray
            };
            transformArray[workspaceId] = Object;
        }    
        else{
            const count = uniqueCoordinates.filter(num => num === lat).length;
            listingCounter = count + 1;
            uniqueCoordinates.push(lat);
            commonWorkspaceArray.push(listing);
            const Object = {
                counter: listingCounter,
                listings: commonWorkspaceArray
            };
            transformArray[workspaceId] = Object;
        }
        
        const latRad = (lat * Math.PI) / 180;
        const lngRad = (lng * Math.PI) / 180;
    
        x += Math.cos(latRad) * Math.cos(lngRad);
        y += Math.cos(latRad) * Math.sin(lngRad);
        z += Math.sin(latRad);
    }
    const total = listings.length;
        x /= total;
        y /= total;
        z /= total;
     
    const hyp = Math.sqrt(x * x + y * y);
    const lat = Math.atan2(z, hyp);
    const lng = Math.atan2(y, x);

    return {
        lat: (lat * 180) / Math.PI,
        lng: (lng * 180) / Math.PI,
        transformArray
    };
};
export interface MapCluster {
    rep: MyListing;
    members: MyListing[];
    lat: number;
    lng: number;
}

export interface MapBounds {
    north: number;
    south: number;
    east: number;
    west: number;
}

/** Marker position for a listing/workspace row, or null when it has no coords. */
export const getListingLatLng = (
    listing: MyListing,
    type: string | undefined
): { lat: number; lng: number } | null => {
    const isWorkspace = type === 'workspace' || type === 'workspaces';
    let lat: number | undefined;
    let lng: number | undefined;
    if (isWorkspace) {
        lat = listing.workspace?.location?.coordinates?.[1] as number;
        lng = listing.workspace?.location?.coordinates?.[0] as number;
    } else {
        const loc =
            typeof listing.location === 'string'
                ? JSON.parse(listing.location)
                : listing.location;
        lat = loc?.coordinates?.[1] as number;
        lng = loc?.coordinates?.[0] as number;
    }
    if (typeof lat !== 'number' || typeof lng !== 'number') return null;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
};

/**
 * Smallest lat/lng box containing every listing that has coordinates.
 *
 * Country-wide searches spread pins across a whole country, where centring on
 * their average lands the map in empty land between the clusters (the middle
 * of the US is farmland, not workspaces). Fitting this box instead keeps every
 * cluster in view.
 */
export const getListingsBounds = (
    listings: MyListing[] | undefined,
    type: string | undefined
): MapBounds | null => {
    if (!listings || listings.length === 0) return null;

    let north = -90;
    let south = 90;
    let east = -180;
    let west = 180;
    let found = false;

    for (const listing of listings) {
        const point = getListingLatLng(listing, type);
        if (!point) continue;
        found = true;
        north = Math.max(north, point.lat);
        south = Math.min(south, point.lat);
        east = Math.max(east, point.lng);
        west = Math.min(west, point.lng);
    }

    return found ? { north, south, east, west } : null;
};

/**
 * Groups listings whose marker positions fall within `thresholdPx` of each
 * other at the given Google Maps zoom level. Pure function — re-call whenever
 * `zoom` or `listings` change.
 *
 * Uses Mercator world-pixel math (no map projection needed): at zoom Z, the
 * world is `256 * 2^Z` pixels wide, and longitude distortion at latitude L is
 * approximated by `cos(L)`.
 */
export const clusterListingsForMap = (
    listings: MyListing[],
    zoom: number,
    type: string | undefined,
    thresholdPx: number = 50
): MapCluster[] => {
    if (!listings || listings.length === 0) return [];

    const worldPx = 256 * Math.pow(2, zoom);
    const pxPerDeg = worldPx / 360;

    const points = listings
        .map((listing) => {
            const point = getListingLatLng(listing, type);
            if (!point) return null;
            return { listing, lat: point.lat, lng: point.lng };
        })
        .filter(
            (p): p is { listing: MyListing; lat: number; lng: number } =>
                p !== null
        );

    if (points.length === 0) return [];

    const meanLat = points.reduce((s, p) => s + p.lat, 0) / points.length;
    const cosLat = Math.cos((meanLat * Math.PI) / 180);

    const claimed = new Array(points.length).fill(false);
    const clusters: MapCluster[] = [];
    const t2 = thresholdPx * thresholdPx;

    for (let i = 0; i < points.length; i++) {
        if (claimed[i]) continue;
        claimed[i] = true;
        const members: MyListing[] = [points[i].listing];
        for (let j = i + 1; j < points.length; j++) {
            if (claimed[j]) continue;
            const dx = (points[j].lng - points[i].lng) * pxPerDeg * cosLat;
            const dy = (points[j].lat - points[i].lat) * pxPerDeg;
            if (dx * dx + dy * dy <= t2) {
                members.push(points[j].listing);
                claimed[j] = true;
            }
        }
        clusters.push({
            rep: points[i].listing,
            members,
            lat: points[i].lat,
            lng: points[i].lng
        });
    }

    return clusters;
};

export const myBookings = (
    bookings: Booking[]
) => {
    const Pending:Booking[] = [];
    const Upcoming:Booking[] = [];
    const Ongoing:Booking[] = [];
    const History:Booking[] = [];

    for (const booking of bookings) {
        if (BookingStatusTabs.pending == 'pending' && BookingStatusArray[BookingStatusTabs.pending].includes(booking.status)) {
            Pending.push(booking);
        }
        else if (BookingStatusTabs.upcoming == 'upcoming' && BookingStatusArray[BookingStatusTabs.upcoming].includes(booking.status)) {
            Upcoming.push(booking);
        }
        else if (BookingStatusTabs.ongoing == 'ongoing' && BookingStatusArray[BookingStatusTabs.ongoing].includes(booking.status)) {
            Ongoing.push(booking);
        }
        else if (BookingStatusTabs.history == 'history' && BookingStatusArray[BookingStatusTabs.history].includes(booking.status)) {
            History.push(booking);
        }
    }
    return {
        pending: Pending,
        upcoming: Upcoming,
        ongoing: Ongoing,
        history: History
    };
};

/** Clean up raw address strings from the API before display. */
export function formatAddressForDisplay(address: string): string {
    return address
        .replace(/^,\s*/, '')
        .replace(/\s*,\s*/g, ', ')
        .replace(/\s{2,}/g, ' ')
        .trim();
}

/** Split an address into clauses so each can resolve bidi (Arabic/English) independently. */
export function splitAddressClauses(address: string): string[] {
    return formatAddressForDisplay(address)
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean);
}